<template>
  <div class="mb-7 mt-48">
    <slot
      :current-step
      name="sticky-bar"
      :title="selectedTitle"
      :total-steps="funnel.length"
    />

    <aside v-for="step in funnel" :key="step.id" class="mb-48">
      <Step
        v-if="step.id !== 'trade_in_jp'"
        :is-premium-definition-updated
        :pickers
        :product
        :step
      />
      <StepTradeInJP v-else />
    </aside>

    <ErrorBoundary :owners="['bot-squad-partnerships-front']">
      <RakutenStep class="mb-48" :pickers :product />
    </ErrorBoundary>
  </div>

  <ClientOnly>
    <NewBatteryModal
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :device-name="product.titles.raw"
    />
    <SimModal v-if="showSimModal" />
    <MacbookProcessorModal v-if="showMacbookProcessorModal" />
    <LaptopProcessorModal v-if="showLaptopProcessorModal" />
    <ConditionModal
      v-if="gradeStep"
      :key="grade"
      :brand="product.brand"
      :grade
      :is-premium-definition-updated
      :step="gradeStep"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useState } from '#imports'
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'

import {
  MACBOOKS_CATEGORIES,
  ProductCategory,
} from '~/scopes/product/constants'
import NewBatteryModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/NewBatteryModal.vue'
import LaptopProcessorModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/LaptopProcessorModal/LaptopProcessorModal.vue'
import MacbookProcessorModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/MacbookProcessorModal.vue'
import SimModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/SimModal/SimModal.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import StepTradeInJP from '~/scopes/tradein-au/components/CustomizationFunnelStep/StepJP.vue'

import ConditionModal from '../ConditionModal/ConditionModal.vue'

import RakutenStep from './components/RakutenStep/RakutenStep.vue'
import Step from './components/Step/Step.vue'
import { createCustomizationFunnelData } from './utils/createCustomizationFunnelData'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    pickers: GetPickersResponse
    isServicesPickersResponsePending: boolean
    price: Price
    priceWithoutSubsidies?: Price | null
    priceWithoutTradeIn?: Price | null
    product: GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
    isPremiumDefinitionUpdated?: boolean
    useBatteryPickerUpdated?: boolean
  }>(),
  {
    priceWithoutSubsidies: undefined,
    priceWithoutTradeIn: null,
    useBatteryPickerUpdated: false,
  },
)

const i18n = useI18n()
const currentStep = useState('current-step', () => 0)
const { grade } = useUrlParams()
const experiments = useExperiments()

const funnel = computed(() => {
  return createCustomizationFunnelData(
    i18n,
    props.pickers.pickerGroups,
    {
      useBatteryPickerUpdated: props.useBatteryPickerUpdated,
    },
    experiments,
  )
})

const selectedOptions = computed(() => {
  const variants = funnel.value
    ?.reduce<Array<string>>((acc, { options, type }) => {
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)

  return variants
})

const selectedTitle = computed(() => {
  return selectedOptions.value.join(' - ')
})

const showSimModal = computed(() => {
  return props.pickers.pickerGroups?.some(({ id }) => id === 'dual_sim')
})

const gradeStep = computed(() => {
  return funnel.value.find(({ id }) => id === 'grades')
})

const showProcessorModal = computed(() => {
  return props.pickers.pickerGroups?.some(
    ({ id }) =>
      id === 'processor_type_and_speed' ||
      id === 'processor_type_and_graphic_card' ||
      id === 'processor',
  )
})

const showMacbookProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    MACBOOKS_CATEGORIES.includes(props.product.tracking.categoryId)
  )
})

const showLaptopProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    ProductCategory.LAPTOP === props.product.tracking.categoryId
  )
})
</script>
